type AudioContextLike = AudioContext;

export class AudioManager {
  private static instance: AudioManager | undefined;
  private audioContext: AudioContextLike | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private ambientNodes: { osc: OscillatorNode; gain: GainNode; lfo?: OscillatorNode }[] = [];

  private sfxVolume = 0.5;
  private ambientVolume = 0.06;
  private isMuted = false;

  private constructor() {
    this.initAudio();
  }

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  private initAudio(): void {
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      this.audioContext = new Ctx();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.7;
      this.masterGain.connect(this.audioContext.destination);
      this.sfxGain = this.audioContext.createGain();
      this.sfxGain.gain.value = this.sfxVolume;
      this.sfxGain.connect(this.masterGain);
      this.ambientGain = this.audioContext.createGain();
      this.ambientGain.gain.value = this.ambientVolume;
      this.ambientGain.connect(this.masterGain);
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
    }
  }

  resumeContext(): void {
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().catch((err) => {
        console.warn('AudioContext resume blocked:', err);
      });
    }
  }

  playEmailChime(): void {
    // Soft single tone — unobtrusive notification.
    this.playTone(660, 0.18, 'sine', 0.25);
    this.playTone(880, 0.12, 'sine', 0.15, 0.05);
  }

  playSlackChimeJamie(): void {
    // Warm major-third chime — Jamie's voice. Distinct from email and CFO chimes.
    this.playTone(523.25, 0.22, 'sine', 0.24);
    this.playTone(659.25, 0.2, 'sine', 0.2, 0.08);
  }

  playSlackChimeCfo(): void {
    // Sharper minor-second — CFO Priya. Cuts through, ends the phrase.
    this.playTone(740, 0.16, 'triangle', 0.22);
    this.playTone(880, 0.12, 'triangle', 0.18, 0.04);
  }

  playSlackChimeMarcus(): void {
    // Neutral mid-tone — Marcus from Billing. Gentle, generic.
    this.playTone(587.33, 0.18, 'sine', 0.2);
  }

  playKeyboardClack(): void {
    // Single short percussive click — a coworker typing nearby.
    this.playTone(160 + Math.random() * 80, 0.04, 'square', 0.05);
  }

  playTeachPanelChime(): void {
    // Gentle two-note pulse — teach panel slides in.
    this.playTone(880, 0.14, 'sine', 0.2);
    this.playTone(1320, 0.1, 'sine', 0.16, 0.06);
  }

  playFiveOClockSilence(): void {
    // Very subtle final tone — used at the 5pm transition.
    this.playTone(220, 0.6, 'sine', 0.18);
  }

  startAmbient(): void {
    if (!this.audioContext || !this.ambientGain) return;
    if (this.ambientNodes.length > 0) return;

    // Layer 1: low fluorescent hum (60Hz mains-adjacent fundamental + 5th harmonic).
    const hum1 = this.audioContext.createOscillator();
    const hum1Gain = this.audioContext.createGain();
    hum1.type = 'sine';
    hum1.frequency.value = 80;
    hum1Gain.gain.value = 0.4;
    hum1.connect(hum1Gain);
    hum1Gain.connect(this.ambientGain);
    hum1.start();
    this.ambientNodes.push({ osc: hum1, gain: hum1Gain });

    const hum2 = this.audioContext.createOscillator();
    const hum2Gain = this.audioContext.createGain();
    hum2.type = 'sine';
    hum2.frequency.value = 240;
    hum2Gain.gain.value = 0.08;
    // Slow LFO modulating amplitude for organic flicker
    const lfo = this.audioContext.createOscillator();
    const lfoGain = this.audioContext.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = 0.13;
    lfoGain.gain.value = 0.04;
    lfo.connect(lfoGain);
    lfoGain.connect(hum2Gain.gain);
    hum2.connect(hum2Gain);
    hum2Gain.connect(this.ambientGain);
    hum2.start();
    lfo.start();
    this.ambientNodes.push({ osc: hum2, gain: hum2Gain, lfo });

    // Layer 2: distant office murmur (filtered noise via fast oscillator + low-passing).
    const murmur = this.audioContext.createOscillator();
    const murmurGain = this.audioContext.createGain();
    const murmurFilter = this.audioContext.createBiquadFilter();
    murmur.type = 'sawtooth';
    murmur.frequency.value = 60;
    murmurFilter.type = 'lowpass';
    murmurFilter.frequency.value = 350;
    murmurFilter.Q.value = 0.4;
    murmurGain.gain.value = 0.07;
    murmur.connect(murmurFilter);
    murmurFilter.connect(murmurGain);
    murmurGain.connect(this.ambientGain);
    murmur.start();
    this.ambientNodes.push({ osc: murmur, gain: murmurGain });
  }

  stopAmbient(): void {
    for (const { osc, gain, lfo } of this.ambientNodes) {
      try {
        gain.gain.linearRampToValueAtTime(0, (this.audioContext?.currentTime ?? 0) + 0.4);
        osc.stop((this.audioContext?.currentTime ?? 0) + 0.5);
        if (lfo) lfo.stop((this.audioContext?.currentTime ?? 0) + 0.5);
      } catch {
        // already stopped
      }
    }
    this.ambientNodes = [];
  }

  playModalOpen(): void {
    // Brief rising glide — signals surface slides in.
    this.playTone(520, 0.12, 'sine', 0.22);
    this.playTone(780, 0.1, 'sine', 0.18, 0.06);
  }

  playModalClose(): void {
    // Brief falling glide — the inverse of open.
    this.playTone(520, 0.12, 'sine', 0.22);
    this.playTone(340, 0.1, 'sine', 0.18, 0.06);
  }

  playButtonClick(): void {
    this.playTone(600, 0.05, 'sine', 0.2);
  }

  playPhoneRing(): void {
    // Realistic 3-pulse ring.
    for (let i = 0; i < 3; i++) {
      this.playTone(440, 0.1, 'sine', 0.3, i * 0.15);
      this.playTone(480, 0.1, 'sine', 0.3, i * 0.15 + 0.05);
    }
  }

  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume = 0.5,
    delay = 0,
  ): void {
    if (!this.audioContext || !this.sfxGain || this.isMuted) return;
    this.resumeContext();

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.type = type;
    oscillator.frequency.value = frequency;

    gainNode.gain.value = 0;
    gainNode.gain.setValueAtTime(0, this.audioContext.currentTime + delay);
    gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + delay + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + delay + duration);

    oscillator.connect(gainNode);
    gainNode.connect(this.sfxGain);

    oscillator.start(this.audioContext.currentTime + delay);
    oscillator.stop(this.audioContext.currentTime + delay + duration + 0.1);
  }

  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.sfxVolume;
    }
  }

  toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (this.masterGain) {
      this.masterGain.gain.value = this.isMuted ? 0 : 0.7;
    }
    return this.isMuted;
  }

  getMuted(): boolean {
    return this.isMuted;
  }
}
